import type { PaymentProviderId } from '@/payment/provider-id';

/**
 * Interval types for subscription plans
 */
export type PlanInterval = PlanIntervals.MONTH | PlanIntervals.YEAR;

export enum PlanIntervals {
  MONTH = 'month',
  YEAR = 'year',
}

/**
 * Payment type (subscription or one-time)
 */
export type PaymentType = PaymentTypes.SUBSCRIPTION | PaymentTypes.ONE_TIME;

export enum PaymentTypes {
  SUBSCRIPTION = 'subscription',  // Regular recurring subscription
  ONE_TIME = 'one_time',          // One-time payment
}

/**
 * Payment scene (credit, subscription)
 */
export type PaymentScene = PaymentScenes.CREDIT | PaymentScenes.SUBSCRIPTION;

export enum PaymentScenes {
  CREDIT = 'credit',          // Credit package purchase
  SUBSCRIPTION = 'subscription', // Regular subscription
}

/**
 * Status of a payment or subscription
 */
export type PaymentStatus =
  | 'active'                         // Subscription is active
  | 'canceled'                       // Subscription has been canceled
  | 'incomplete'                     // Payment not completed
  | 'incomplete_expired'             // Payment not completed and expired
  | 'superseded'                     // Replaced by a newer checkout flow
  | 'past_due'                       // Payment is past due
  | 'paused'                         // Subscription is paused
  | 'unpaid'                         // Payment failed
  | 'completed'                      // One-time payment completed
  | 'processing'                     // Payment is processing
  | 'failed';                        // Payment failed

export const PAYMENT_STATUSES: PaymentStatus[] = [
  'active',
  'canceled',
  'incomplete',
  'incomplete_expired',
  'superseded',
  'past_due',
  'paused',
  'unpaid',
  'completed',
  'processing',
  'failed',
];

export function isPaymentStatus(value: string): value is PaymentStatus {
  return PAYMENT_STATUSES.includes(value as PaymentStatus);
}

/**
 * Price definition for a plan
 */
export interface Price {
  type: PaymentType;                 // Type of payment (subscription or one_time)
  priceId: string;                   // Internal stable price SKU
  amount: number;                    // Price amount in minor units (cents)
  currency: string;                  // Currency code (e.g., USD)
  interval?: PlanInterval;           // Billing interval for recurring payments
  disabled?: boolean;                // Whether to disable this price in UI
}

/**
 * Credits configuration for a plan
 */
export interface Credits {
  enable: boolean;                   // Whether to enable credits for this plan
  amount: number;                    // Number of credits provided per month
  expireDays?: number;               // Number of days until credits expire, undefined means no expiration
}

/**
 * Price plan definition
 *
 * 1. When to set the plan disabled?
 * When the plan is not available anymore, but you should keep it for existing users
 * who have already purchased it, otherwise they can not see the plan in the Billing page.
 *
 * 2. When to set the price disabled?
 * When the price is not available anymore, but you should keep it for existing users
 * who have already purchased it, otherwise they can not see the price in the Billing page.
 */
export interface PricePlan {
  id: string;                        // Unique identifier for the plan
  name?: string;                     // Display name of the plan
  description?: string;              // Description of the plan features
  features?: string[];               // List of features included in this plan
  limits?: string[];                 // List of limits for this plan
  prices: Price[];                   // Available prices for this plan
  isFree: boolean;                   // Whether this is a free plan
  popular?: boolean;                 // Whether to mark this plan as popular in UI
  disabled?: boolean;                // Whether to disable this plan in UI
  credits?: Credits;                 // Credits configuration for this plan
}

/**
 * Customer data
 */
export interface Customer {
  id: string;
  email: string;
  name?: string;
  metadata?: Record<string, string>;
}

/**
 * Subscription data
 */
export interface Subscription {
  id: string;
  customerId: string;
  provider: PaymentProviderId;
  status: PaymentStatus;
  priceId: string;
  type: PaymentType;
  interval?: PlanInterval;
  currentPeriodStart?: Date;
  currentPeriodEnd?: Date;
  accessEndAt?: Date;
  cancelAtPeriodEnd?: boolean;
  createdAt: Date;
}

/**
 * Payment data
 */
export interface Payment {
  id: string;
  customerId: string;
  amount: number; // Amount in minor units (cents)
  currency: string;
  status: PaymentStatus;
  createdAt: Date;
  metadata?: Record<string, string>;
}

/**
 * Parameters for getting customer subscriptions
 */
export interface getSubscriptionsParams {
  userId: string;
}
